import React, { useState, useMemo } from 'react';
import type { PersonalAccidentBeneficiaryData, BeneficiaryDetailsData } from '../../../types/customer-portal';
import { formatFullName } from '../../../utils/nameFormatter';
import './PersonalAccidentBeneficiaryStep.css';

interface PersonalAccidentBeneficiaryStepProps {
  data?: PersonalAccidentBeneficiaryData;
  beneficiaryData?: BeneficiaryDetailsData;
  onNext: (data: PersonalAccidentBeneficiaryData) => void;
  onBack: () => void;
}

const PersonalAccidentBeneficiaryStep: React.FC<PersonalAccidentBeneficiaryStepProps> = ({
  data,
  beneficiaryData,
  onNext,
  onBack
}) => {
  const [isConfirmed, setIsConfirmed] = useState(data?.isConfirmed || false);

  // Form validation - confirmation checkbox must be checked
  const isFormValid = useMemo(() => {
    return isConfirmed;
  }, [isConfirmed]);

  const handleNext = () => {
    if (isFormValid) {
      onNext({ 
        beneficiaries: [], // Not collecting new beneficiaries, just confirming existing ones
        isConfirmed 
      });
    }
  };

  const handleConfirmationChange = (checked: boolean) => {
    setIsConfirmed(checked);
  };

  return (
    <>
      <div className="personal-accident-beneficiary-step">
        {/* Beneficiaries Review Section */}
        <div className="beneficiaries-review-section">
          <p className="review-instruction">
            Please review your relationship to the following beneficiary/ies:
          </p>
          
          <div className="beneficiaries-display">
            {beneficiaryData?.beneficiaries && beneficiaryData.beneficiaries.length > 0 ? (
              beneficiaryData.beneficiaries.map((beneficiary) => (
                <div key={beneficiary.id} className="beneficiary-item">
                  <div className="beneficiary-name">
                    {formatFullName(beneficiary)}
                  </div>
                  <div className="beneficiary-relationship">
                    {beneficiary.relationship}
                  </div>
                </div>
              ))
            ) : (
              <div className="no-beneficiaries-message">
                No beneficiaries found from previous step.
              </div>
            )}
          </div>
        </div>

        {/* Family Benefits Section */}
        <div className="family-benefits-section">
          <h4 className="family-benefits-title">Family Benefits</h4>
          
          <div className="benefits-list">
            <div className="benefit-item">
              <span className="benefit-number">1.</span>
                <span className="benefit-text">
                  The legal spouse and children are entitled to the same benefits as the Principal Insured Person (e.g., either of the parents), except for:  
                </span>
            </div>
            <div className="benefit-item">            
                <div className="benefit-content">
                <ul className="benefit-sub-list">
                  <li>Children under six (6) years old. Entitled to a Personal Accident Benefit limit of up to PHP100,000, USD5,000 or EUR4,500 for all Peso, Dollar and Euro Plans, respectively.  </li>
                  <li>Children age six (6) to 15 years old. Entitled to a Personal Accident Benefit limit of up to PHP1,000,000, USD20,000 or EUR18,000 for all Peso, Dollar and Euro Plans, or up to Policy limits, whichever is lower.  </li>
                </ul>
              </div>
            </div>
            <div className="benefit-item">
              <span className="benefit-number">2.</span>
              <span className="benefit-text">
                Maximum Personal Accident Benefit Limit of the entire Family: Up to 300% of the Principal Insured’s benefit.  
              </span>
            </div>
            
            <div className="benefit-item">
              <span className="benefit-number">3.</span>
              <span className="benefit-text">
                Maximum Benefit Limit for all other benefits of the entire Family (including Principal Insured): Up to 400% of the Principal Insured’s benefit.  
              </span>
            </div>
          </div>
        </div>

        {/* Confirmation Checkbox */}
        <div className="confirmation-section">
          <label className="confirmation-checkbox">
            <input
              type="checkbox"
              checked={isConfirmed}
              onChange={(e) => handleConfirmationChange(e.target.checked)}
            />
            <span className="checkmark"></span>
            <span className="confirmation-text">
              I have read and reviewed the above information and confirm that these are correct.  
            </span>
          </label>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="step-navigation">
        <button
          type="button"
          className="btn-back"
          onClick={onBack}
        >
          BACK
        </button>
        <button
          type="button"
          className={`btn-next ${!isFormValid ? 'disabled' : ''}`}
          onClick={handleNext}
          disabled={!isFormValid}
        >
          NEXT
        </button>
      </div>
          {/* Footer */}
          <span className='assistance-footer'>
            Need assistance? Email us at  <span className="email-text">traveldirect_team@pacificcross.com.ph</span> 
          </span>
    </>
  );
};

export default PersonalAccidentBeneficiaryStep;